import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Menu/popover dismissal (FRONTEND_DESIGN.md §4.5, simplified): closes on a press outside the panel
 * and its trigger, and on Escape (the innermost open one first). Opening one closes any other, except
 * the ones it sits inside (a menu in the *More* menu keeps *More* open). `panelRef` is for a panel
 * rendered elsewhere (a portal), so presses inside it still count as inside.
 */
interface Opened {
  close(): void;
  ref: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
}
const opened = new Set<Opened>();

/** A modal dialog opened above this popover (from inside it, e.g. the filter builder from a widget's settings). */
const modalAbove = (self: Opened) =>
  [...document.querySelectorAll("[data-modal]")].some((m) => !holds(self, m));

const holds = (o: Opened, el: Element | null) =>
  !!el && (!!o.ref.current?.contains(el) || !!o.panelRef.current?.contains(el));

export function usePopover<T extends HTMLElement = HTMLDivElement>(): {
  open: boolean;
  setOpen(v: boolean): void;
  toggle(): void;
  ref: RefObject<T | null>;
  panelRef: RefObject<HTMLElement | null>;
} {
  const [open, setOpenState] = useState(false);
  const ref = useRef<T | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const setOpen = (v: boolean) => {
    if (v) for (const o of opened) if (!holds(o, ref.current)) o.close();
    setOpenState(v);
  };
  useEffect(() => {
    if (!open) return;
    const self: Opened = { close: () => setOpenState(false), ref, panelRef };
    opened.add(self);
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      // a press in a dialog opened above it is not "outside"
      if ((t as Element).closest?.("[data-modal]") && modalAbove(self)) return;
      if (ref.current && !ref.current.contains(t) && !panelRef.current?.contains(t)) self.close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (modalAbove(self)) return; // the dialog above closes first
      // only the innermost open popover closes; the one around it waits for the next Escape
      for (const o of opened) if (o !== self && holds(self, o.ref.current)) return;
      self.close();
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      opened.delete(self);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, toggle: () => setOpen(!open), ref, panelRef };
}
